import { Link } from "react-router-dom";
import { Check, Clock, MessageCircle } from "lucide-react";
import { BtnLink, Eyebrow, Reveal } from "./Primitives";
import { CONTACT, useLink, useT } from "@/lib/i18n";

export const Marquee = () => {
  const t = useT();
  const words = [...t.marquee, ...t.marquee];
  return (
    <div className="overflow-hidden border-y border-forest/10 bg-ivory py-6" aria-hidden="true">
      <div className="marquee-track flex w-max items-center gap-10 whitespace-nowrap">
        {words.map((w, i) => (
          <span key={i} className="flex items-center gap-10 font-display text-3xl italic text-forest/80 sm:text-4xl">
            {w}<span className="h-2 w-2 rounded-full bg-gold" />
          </span>
        ))}
      </div>
    </div>
  );
};

export const DiscoveryBand = ({ title }) => {
  const t = useT();
  const link = useLink();
  const d = t.discovery;
  return (
    <section data-testid="discovery-section" className="container-x py-20 sm:py-28">
      <Reveal>
        <div className="relative overflow-hidden rounded-[36px] bg-forest text-ivory">
          <img src="/img/coast-sunset.jpg" alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-forest via-forest/95 to-forest/60" />
          <div className="relative grid gap-12 p-8 sm:p-12 lg:grid-cols-12 lg:p-16">
            <div className="lg:col-span-7">
              <Eyebrow light>{d.eyebrow}</Eyebrow>
              <h2 className="mt-5 font-display text-4xl leading-[1.02] sm:text-5xl lg:text-6xl">{title || d.title}</h2>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-ivory/85 sm:text-lg">{d.text}</p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <BtnLink to={link("apply")} variant="ivory" data-testid="discovery-apply-btn">{d.cta}</BtnLink>
                <span className="flex items-center gap-2 text-sm text-ivory/80"><Clock size={16} /> {t.apply.time}</span>
              </div>
              <p className="mt-8 max-w-xl border-l border-gold-light/60 pl-4 text-sm leading-relaxed text-ivory/80" data-testid="discovery-note">{d.note}</p>
            </div>
            <ol className="flex flex-col justify-center gap-4 lg:col-span-5">
              {d.steps.map((s, i) => (
                <li key={s} className="flex items-center gap-5 rounded-2xl border border-ivory/15 bg-ivory/5 p-5 backdrop-blur-sm">
                  <span className="font-display text-4xl italic text-gold-light">0{i + 1}</span>
                  <span className="text-[15px] leading-snug">{s}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </Reveal>
    </section>
  );
};

export const CheckList = ({ items, light = false }) => (
  <ul className="space-y-3">
    {items.map((it) => (
      <li key={it} className={`flex items-start gap-3 text-[15px] leading-snug ${light ? "text-ivory/90" : "text-forest/85"}`}>
        <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${light ? "bg-gold-light/20 text-gold-light" : "bg-forest/10 text-forest"}`}>
          <Check size={12} strokeWidth={3} />
        </span>
        {it}
      </li>
    ))}
  </ul>
);

export const PageHero = ({ eyebrow, title, intro, image, imageAlt, media, testid = "page-hero" }) => (
  <section data-testid={testid} className="relative overflow-hidden pb-16 pt-32 sm:pt-40 lg:pb-24">
    <div className="container-x grid items-end gap-12 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <Reveal><Eyebrow>{eyebrow}</Eyebrow></Reveal>
        <Reveal delay={0.08}>
          <h1 className="mt-6 font-display text-[2.7rem] leading-[1] text-forest sm:text-6xl lg:text-[4.6rem]">{title}</h1>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-7 max-w-2xl text-base leading-relaxed text-forest/80 sm:text-lg">{intro}</p>
        </Reveal>
      </div>
      <div className="lg:col-span-5">
        <Reveal delay={0.1}>
          {media || <img src={image} alt={imageAlt} className="arch h-[340px] w-full object-cover sm:h-[460px]" />}
        </Reveal>
      </div>
    </div>
  </section>
);

export const InlineLink = ({ to, children, testid }) => (
  <Link to={to} data-testid={testid} className="link-u inline-flex items-center gap-2 font-medium text-forest">{children}</Link>
);

export const WhatsAppButton = () => {
  const t = useT();
  return (
    <a
      href={CONTACT.whatsappHref}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t.whatsapp}
      data-testid="whatsapp-float-btn"
      className="group fixed bottom-5 right-5 z-[65] flex h-14 items-center gap-2 rounded-full bg-[#1f7a4d] pl-4 pr-4 text-white shadow-[0_14px_30px_-10px_rgba(23,56,46,.6)] transition-[transform,padding] duration-300 hover:-translate-y-0.5 sm:pr-5"
    >
      <MessageCircle size={24} strokeWidth={1.8} />
      <span className="hidden text-sm font-medium sm:inline">{t.whatsapp}</span>
    </a>
  );
};
